
























import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import Blurb from 'frontend/components/containers/Blurb.vue';
import Modal from 'frontend/components/Modal.vue';
import { mapGetters } from 'vuex';
import { trackClickWithElementId, trackModalWithElementId } from 'frontend/utils/nux';
import { updateProfileFlag } from 'frontend/utils/flags';

interface ReferAFriendModalContent {
  title: string;
  description: string;
  buttonText: string;
  buttonHandler: () => void;
}

@Component({
  components: {
    Blurb,
    Modal,
  },
  computed: {
    ...mapGetters('globals', ['isAuthenticated']),
  },
})
export default class ReferAFriendModal extends Vue {
  @Prop({ default: 3 }) freeMonthsCount!: number;
  @Prop({ default: 'referrer' }) as!: 'referrer' | 'referee' | 'legacyReferrer';
  @Prop() referrerName: string | undefined;
  @Prop({ default: false }) legacyLoggedIn!: boolean;
  @Prop({ default: true }) private readonly isLessonModal!: boolean;

  private loading = false;

  private isAuthenticated!: boolean;

  private async mounted() {
    switch (this.as) {
      case 'referrer':
        const elementId = this.isLessonModal
          ? 'viewed_lesson_referral_modal'
          : 'viewed_referral_modal';
        trackModalWithElementId(elementId);
        break;
      case 'legacyReferrer':
        // popped after completing 2nd lesson. Update flag so we don't show the modal again
        // if in the future we have other cases that pop this modal as a legacy call you'll need to
        // update this to check if the reason for popping it was the 2nd lesson case otherwise some
        // other case might trigger the flag set and the user won't see the modal when they complete
        // the lesson :(
        await updateProfileFlag('has_seen_refer_a_friend_modal_after_2nd_lesson');
        break;
    }
  }

  private get content() {
    // logged out content
    const contentFor: { [key: string]: ReferAFriendModalContent } = {
      referrer: {
        title: 'Give free months, get free months',
        description: `Share ${this.freeMonthsCount} free months of Premium with your friends, and earn
            ${this.freeMonthsCount} free months for yourself when they subscribe.`,
        buttonText: 'Refer a friend',
        buttonHandler: () => {
          const elementId = this.isLessonModal
            ? 'clicked_refer_friend_lesson_modal'
            : 'clicked_refer_friend_modal';
          trackClickWithElementId('clicked_refer_friend_lesson_modal');
          this.$router.push({ name: 'referrer_home' });
        },
      },
      legacyReferrer: {
        title: 'Give free months, get free months',
        description: `Share ${this.freeMonthsCount} free months of Premium with your friends, and earn
            ${this.freeMonthsCount} free months for yourself when they subscribe.`,
        buttonText: 'Refer a friend',
        buttonHandler: () => {
          this.loading = true;
          this.$emit('external', { name: 'cta-clicked' });
        },
      },
      referee: {
        title: `${this.referrerName} gifted you ${this.freeMonthsCount} free months`,
        description: `Get ${this.freeMonthsCount} free months off your first year of Brilliant Premium thanks
             to ${this.referrerName}`,
        buttonText: "Let's go",
        buttonHandler: () => {
          this.loggedIn
            ? this.redirectToPremiumOrJustCloseIfAlreadyThere()
            : this.trackCloseAndPopSignupModal();
        },
      },
    };
    // go through referral link pipeline to apply overrides. If we need to make more changes just
    // add another pipeline that takes a ReferAFriendModalContent and returns a modified
    // ReferAFriendModalContent
    return this.loggedInPipeline(contentFor[this.as]);
  }

  private loggedInPipeline(content: ReferAFriendModalContent): ReferAFriendModalContent {
    if (!this.loggedIn) {
      return content;
    }
    const loggedInOverrides = {
      referrer: {},
      referee: {
        description: `Get ${this.freeMonthsCount} free months off your first year of Brilliant Premium for
            the next 15 days.`,
        buttonText: 'Redeem Offer',
      },
      legacyReferrer: {},
    };
    return Object.assign(content, loggedInOverrides[this.as]);
  }

  private bubbleClose(): void {
    if (this.as === 'referrer') {
      trackClickWithElementId('closed_refer_friend_lesson_modal');
    }
    this.$emit('external', { name: 'close' });
    this.$emit('close');
  }

  private redirectToPremiumOrJustCloseIfAlreadyThere(): void {
    trackClickWithElementId('clicked_redeem_from_referral_logged_in');
    if (this.$route.name === 'chargebee_paywall') {
      // already in /premium just close
      this.bubbleClose();
    } else {
      this.$router.push({ name: 'chargebee_paywall' });
    }
  }

  private trackCloseAndPopSignupModal(): void {
    trackClickWithElementId('clicked_lets_go_from_referral_logged_out');
    this.bubbleClose();
    this.$store.dispatch('signupModal/signup');
  }

  private get loggedIn() {
    if (this.as === 'legacyReferrer') {
      return this.legacyLoggedIn;
    }
    return this.isAuthenticated;
  }
}
